import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@monorepo/shared-auth';
import Button from '../../components/common/Button/Button';
import SportIcon from '../../components/common/SportIcon/SportIcon';
import popularSports from '../../data/popularSports';

const ProfileSetup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    city: '',
    yearOfBirth: '',
    favoriteSports: []
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { currentUser, createUserProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || '/';

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const toggleSport = (sportId) => {
    setFormData((prev) => {
      const isSelected = prev.favoriteSports.includes(sportId);

      if (isSelected) {
        return {
          ...prev,
          favoriteSports: prev.favoriteSports.filter((id) => id !== sportId)
        };
      } else {
        if (prev.favoriteSports.length >= 3) {
          return prev;
        }
        return {
          ...prev,
          favoriteSports: [...prev.favoriteSports, sportId]
        };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.favoriteSports.length === 0) {
      setError('Please select at least one favorite sport');
      return;
    }

    setLoading(true);

    try {
      await createUserProfile(currentUser.uid, {
        email: currentUser.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        city: formData.city,
        yearOfBirth: parseInt(formData.yearOfBirth),
        favoriteSports: formData.favoriteSports
      });

      navigate(returnTo);
    } catch (err) {
      setError('Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="ub-profile-setup-page">
      <div className="ub-profile-setup-container">
        <div className="ub-profile-setup-card">
          <h1 className="ub-profile-setup-title">Complete Your Profile</h1>
          <p className="ub-profile-setup-subtitle">
            Help us personalize your UBsports experience
          </p>

          {error && <div className="ub-profile-setup-error">{error}</div>}

          <form onSubmit={handleSubmit} className="ub-profile-setup-form">
            <div className="ub-form-row">
              <div className="ub-form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder="John"
                />
              </div>

              <div className="ub-form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder="Doe"
                />
              </div>
            </div>

            <div className="ub-form-group">
              <label htmlFor="city">City *</label>
              <p className="ub-field-hint">
                Used to help you find local sporting events
              </p>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                placeholder="San Francisco"
              />
            </div>

            <div className="ub-form-group">
              <label htmlFor="yearOfBirth">Year of Birth *</label>
              <select
                id="yearOfBirth"
                name="yearOfBirth"
                value={formData.yearOfBirth}
                onChange={handleInputChange}
                required
              >
                <option value="">Select year</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="ub-form-group">
              <label>Favorite Sports (Select up to 3) *</label>
              <p className="ub-field-hint">
                Selected: {formData.favoriteSports.length}/3
              </p>
              <div className="ub-sports-grid">
                {popularSports.map((sport) => (
                  <button
                    key={sport.id}
                    type="button"
                    onClick={() => toggleSport(sport.id)}
                    className={`ub-sport-option ${
                      formData.favoriteSports.includes(sport.id) ? 'ub-selected' : ''
                    }`}
                  >
                    <SportIcon iconName={sport.icon} size={24} />
                    <span>{sport.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="large"
              disabled={loading}
              className="ub-profile-submit ub-button-style-one"
            >
              {loading ? 'Creating Profile...' : 'Complete Setup'}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
};

export default ProfileSetup;
